import { Capacitor } from "@capacitor/core";

/**
 * InBody results export for the web app. The formatter lives in
 * supabase/functions/_shared/inbody-export.ts so the email-inbody-results
 * edge function builds exactly the same files the download buttons do.
 */
export {
  inbodyScansToCsv,
  inbodySummaryMarkdown,
  inbodyExportFilename,
  inbodyStatus,
  INBODY_GUIDANCE,
  type InbodyExportRow,
  type InbodyStatus,
} from "../../supabase/functions/_shared/inbody-export";

export type SaveResult = "downloaded" | "shared" | "cancelled" | "failed";

/**
 * Save text as a file. Same rules as the PDF report
 * (src/lib/inbodyReport/download.ts): in the phone app an anchor download
 * does nothing, so there it opens the share sheet, and says "failed" when the
 * app can't share files rather than claiming it worked.
 */
export async function downloadTextFile(
  filename: string,
  text: string,
  mime: string,
): Promise<SaveResult> {
  if (Capacitor.isNativePlatform()) {
    try {
      const file = new File([text], filename, { type: mime });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "my ZUZU inbody results",
        });
        return "shared";
      }
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") return "cancelled";
    }
    return "failed";
  }
  try {
    // The CSV already carries its UTF-8 BOM (added in inbodyScansToCsv), so
    // the download and the emailed copy are byte-for-byte the same.
    const url = URL.createObjectURL(new Blob([text], { type: mime }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

/** Copy to the clipboard. Returns false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  return false;
}
